import React from "react";

import { EditableRegion } from "../form/EditableRegion";
import { closestIndex } from "../utils/dom";
import { Table } from "./Table";
import { generateUniqueShortID } from "../utils/unique";
import { DataType } from "../form/types";
import { useConditional } from "../hooks/useConditional";
import { Modal } from "./Modal";
import { IconChoice, IconChoiceModal } from "./IconChoice";
import { Icon } from "./icons";
import { formatCurrency } from "../../formatters/format";
import { last } from "../utils/array";

interface TableColumn {
	id: string,
	title: string,
	datatype?: DataType,
}

interface TableData {
	columns: TableColumn[],
	rows: {
		[k: string]: string,
	}[],
}


export function arrayToTable(a: any[], columns: TableColumn[]) {
	const result = {
		columns,
		rows: [],
	} as any;

	for(let r of a) {
		const obj = {} as any;
		
		for(let c of result.columns) {
			obj[c.id] = r[c.id];
		}
		
		result.rows.push(obj);
	}

	return result;
}

interface EditableTableOptions {
	columnsEditable?: boolean,

	addRowButton?: boolean,
	deleteRowButton?: boolean,
}

export function EditableTable(props: {
	data: TableData,
	options?: EditableTableOptions
}) {

	const [, refresh] = React.useState({});

	const options = props.options ? props.options : {} as EditableTableOptions;

	function addRow(index: number) {
		const newRow = {} as any;
		props.data.rows.splice(index, 0, newRow);
		// init all values to empty
		for(let c of props.data.columns) {
			newRow[c.id] = "";
		}
		refresh({});
	}
	function addCol(index: number) {
		const id = generateUniqueShortID(props.data.columns, "id");
		const newCol = {
			id,
			title: "New Column " + id,
		};
		props.data.columns.splice(index, 0, newCol);

		refresh({});
	}

	function handleDeleteColumn(c: TableColumn) {
		// find index of the column
		let index = props.data.columns.indexOf(c);
		if (index < 0) return;

		// go through and delete all data for this col from all rows
		for(let r of props.data.rows) {
			delete r[c.id];
		}

		// remove col def
		props.data.columns.splice(index, 1);

		refresh({});
	}

	function deleteRow(r: any) {
		for(let i=0;i<props.data.rows.length;i++) {
			const row = props.data.rows[i];
			if (r === row) {
				props.data.rows.splice(i, 1);
				refresh({});
				break;
			}
		}
	}

	const lastCol = last(props.data.columns);

	return (
		<div 
			className="editable-table-container"
			onKeyDown={(e) => {
				if (e.metaKey && !e.shiftKey && !e.altKey && !e.ctrlKey) {
					let index;
					// in a cell?
					index = closestIndex(e.target, ".cell");
					if (index >= 0) {
						if (e.code === "ArrowLeft") {
							addCol(index);
						}
						if (e.code === "ArrowRight") {
							addCol(index+1);
						}

						// if in a row
						index = closestIndex(e.target, ".row");
						if (index >= 0) {
							if (e.code === "ArrowUp") {
								addRow(index);
							}
							if (e.code === "ArrowDown") {
								addRow(index+1);
							}
						}	
					}
				}

			}}
		>
			<Table
				items={props.data.rows}

				cols={props.data.columns}

				render={{
					colTitle: (c) => {
						let contents;

						if (options.columnsEditable !== false) {
							return (
								<div className="flex gap-2">
									<EditableRegion
										intialHtml={c.title}
										onChange={(e) => {
											c.title = e.target.value;
										}}
									/>
									<ColumnDataType col={c} />
									<button 
										className="small error"
										onClick={() => {
											handleDeleteColumn(c);
										}}
									>Delete</button>
								</div>
							);
						} else {
							return (
								<b>{c.title}</b>
							);
						}

					},
					colValue: (r, c) => {
						const contents = (
							<EditableRegion
								className="grow"
								intialHtml={r[c.id]}
								onChange={(e) => {
									let v = e.target.value;
									// if (v) {
									// 	if (c.datatype === "currency") {
									// 		v = formatCurrency(v);
									// 	} else if (c.datatype === "number") {
									// 	}
									// }
									r[c.id] = v;
								}}
							/>
						);

						if (options.deleteRowButton && c === lastCol) {
							return (
								<div className="flex horiz gap-5">
									{contents}
									<button
										className="primary"
										onClick={() => {
											deleteRow(r);
										}}
									>Delete</button>
								</div>
							)
						} else {
							return contents;
						}
					}
				}}
			/>
			{
				options.addRowButton && 
				<button
					className="primary"
					onClick={() => {
						props.data.rows.push({});
						refresh({});
					}}
				>Add</button>
			}
		</div>
	)
}


function ColumnDataType(props: {
	col: TableColumn,
}) {

	const choose = useConditional(() => {
		return (
			<IconChoiceModal
				size="width-50pc"
				options={[
					{
						//id: "string",
						title: "string",
					},
					{
						title: "number",
					},
					{
						title: "boolean",
					},
				]}
				onChoose={(option) => {
					props.col.datatype = option.title as DataType;
					choose.close();
				}}
				onCancel={() => {
					choose.close();
				}}

			/>
		)
	});

	return (
		<>
			<div className="tag"
				onClick={() => {
					choose.show({});
				}}
			>{props.col.datatype ? props.col.datatype : "string"}</div>
			{choose.render()}
		</>
	)

}